// Wishlist hearts on product photos and product cards (snippets/wishlist-button.liquid), the header wishlist
// drawer, and the cart drawer after "Add to cart" in the wishlist.
//
// The Swish app stores the list. Its "App settings" embed loads its script and creates window.WishlistKing.
// The hearts are our own buttons, so their markup, labels and focus follow the theme. This file only asks
// Swish for their state and tells it to add or remove.
//
// Swish does not check cookie consent. This file does: without Personalization (preferences) consent the
// hearts stay hidden and never call Swish. The header wishlist link explains why (header-widgets.js).

// One function scope, so these names cannot clash with the many app scripts on the page.
(function () {
  if (window.svahaWishlist) {
    return;
  }
  window.svahaWishlist = true;

  // Swish creates window.WishlistKing, then fires wk:app:ready on the document. It may already exist.
  let swishReady = null;
  function whenSwishReady() {
    swishReady ??= new Promise((resolve) => {
      if (window.WishlistKing?.state) {
        resolve(window.WishlistKing);
        return;
      }
      document.addEventListener('wk:app:ready', () => resolve(window.WishlistKing), { once: true });
    });
    return swishReady;
  }

  // Resolves true once the visitor allows Personalization. Never resolves while it is off.
  // svahaConsentReady: see snippets/consent-gate.liquid.
  let consentGiven = null;
  function whenConsentGiven() {
    consentGiven ??= (window.svahaConsentReady ?? new Promise(() => {})).then(
      (privacy) =>
        new Promise((resolve) => {
          const check = () => {
            if (privacy.preferencesProcessingAllowed()) {
              document.removeEventListener('visitorConsentCollected', check);
              resolve(true);
            }
          };
          document.addEventListener('visitorConsentCollected', check);
          check();
        }),
    );
    return consentGiven;
  }

  // <svaha-wishlist-button data-product-handle data-product-title [form]>
  // holds one <button>. The button keeps one name ("Save to wishlist: <title>"). aria-pressed says whether the
  // product is in the list, and the heart fills when it is.
  class WishlistButton extends HTMLElement {
    static observedAttributes = ['data-product-handle', 'data-product-title'];

    #button = null;
    #subscription = null;
    #itemId = null;
    #known = false;
    #busy = false;
    #abort = null;

    connectedCallback() {
      this.#button = this.querySelector('button');
      if (!this.#button) {
        return;
      }
      this.#abort = new AbortController();
      const { signal } = this.#abort;
      this.#button.addEventListener('click', () => this.#toggle(), { signal });

      whenConsentGiven()
        .then(whenSwishReady)
        .then(() => {
          if (!this.isConnected) {
            return;
          }
          this.hidden = false;
          this.#watch();
        });
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#subscription?.unsubscribe();
      this.#subscription = null;
    }

    // A swatch on a product card swaps the product (product-card-featured.js). Watch the new one.
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue || !this.#button) {
        return;
      }
      if (name === 'data-product-title') {
        this.#button.setAttribute('aria-label', `${this.dataset.label}: ${newValue}`);
      } else if (!this.hidden) {
        this.#watch();
      }
    }

    // Until Swish answers for the new product, the heart does not know whether it is saved. #known stops a
    // click in that moment from removing the previous product's item or adding the product twice.
    #watch() {
      this.#subscription?.unsubscribe();
      this.#itemId = null;
      this.#known = false;
      this.#setPressed(false);
      this.#subscription = window.WishlistKing.state
        .observeProductInfo({ productHandle: this.dataset.productHandle })
        .subscribe((info) => {
          this.#itemId = info?.inWishlist ? info.wishlistItemId : null;
          this.#known = true;
          this.#setPressed(Boolean(info?.inWishlist));
        });
    }

    #setPressed(pressed) {
      this.#button.setAttribute('aria-pressed', String(pressed));
    }

    // A save keeps a size only when the shopper picked one. Without a size, Swish asks for one at "Add to
    // cart". A card heart has no form, so it never saves a size. On the product page the form's id field
    // holds the first size before any pick, so the picked options decide. A product with no options saves
    // its only variant.
    #variantToSave() {
      const formName = this.getAttribute('form');
      const form = formName ? document.forms[formName] : null;
      const variantId = form?.elements.namedItem('id')?.value;
      if (!variantId) {
        return undefined;
      }
      const picked = new Map();
      for (const input of form.elements) {
        if (input.type === 'radio' && /^option\d$/.test(input.name)) {
          picked.set(input.name, picked.get(input.name) || input.checked);
        }
      }
      return [...picked.values()].every(Boolean) ? variantId : undefined;
    }

    // Swish ignores a second action while one is still running, so clicks wait for the last one.
    async #toggle() {
      if (this.#busy || this.hidden || !this.#known) {
        return;
      }
      this.#busy = true;
      this.#button.setAttribute('aria-busy', 'true');
      try {
        if (this.#itemId) {
          await window.WishlistKing.removeWishlistItem({ wishlistItemId: this.#itemId });
        } else {
          await window.WishlistKing.addWishlistItem({
            productHandle: this.dataset.productHandle,
            variantId: this.#variantToSave(),
          });
        }
      } catch (error) {
        // The heart shows Swish's state, so after a failed save or remove it shows the state that holds.
        console.warn('Wishlist: Swish could not save the change.', error);
      } finally {
        this.#busy = false;
        this.#button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('svaha-wishlist-button')) {
    customElements.define('svaha-wishlist-button', WishlistButton);
  }

  // The header wishlist drawer (sections/header.liquid). Swish's list goes in when the drawer first opens,
  // so a page view that never opens it makes no request to Swish. It goes in only with Personalization
  // consent. header-widgets.js normally stops the drawer from opening without it, but that can fail, for
  // example while the consent API is still loading. The listener is on the document, in the capture phase,
  // so it also covers a drawer that the theme editor renders again. dialog:before-show does not bubble.
  document.addEventListener(
    'dialog:before-show',
    (event) => {
      if (event.target.id !== 'wishlist-drawer') {
        return;
      }
      const drawer = event.target;
      whenConsentGiven().then(() => {
        const template = drawer.querySelector('template[data-wishlist-drawer-content]');
        if (template) {
          template.replaceWith(template.content.cloneNode(true));
        }
      });
    },
    true,
  );

  // "Add to cart" in the wishlist adds through Swish, not the theme. Refresh the theme's cart and open the
  // cart drawer, as the theme does after its own add to cart. The override of Swish's "Add To Cart Action"
  // (docs/swish-code-access/add-to-cart-action.js) keeps Swish from opening /cart instead.
  async function onWishlistAddToCart() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    const wishlistDrawer = document.getElementById('wishlist-drawer');
    if (wishlistDrawer?.open) {
      await wishlistDrawer.hide();
    }
    document.querySelector('#cart-drawer')?.setAttribute('open', '');
  }
  document.addEventListener('wk:wishlist:add-to-cart:success', onWishlistAddToCart);
  document.addEventListener('wk:wishlist:add-all-to-cart:success', onWishlistAddToCart);
})();
